import { useState } from "react";
import { categories } from "../data/categories";
import { colors } from "../data/colors";
import { markings } from "../data/markings";
import { mockups } from "../data/mockups";
import { products } from "../data/products";
import { computeQuote } from "../utils/pricing";
import { useDesign } from "./useDesign";
import type { ConfigState, MarkingId, Product } from "../types/configurator";

export const STEPS = ["Produit", "Personnalisation", "Quantité", "Envoi"] as const;

const initialState: ConfigState = {
  categoryId: null,
  productId: null,
  colorId: null,
  quantity: 0,
  markingId: null,
  logoColors: 2,
  provideLater: false,
};

export function useConfigurator() {
  const [state, setState] = useState<ConfigState>(initialState);
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const design = useDesign();

  const category = categories.find((c) => c.id === state.categoryId) ?? null;
  const product = products.find((p) => p.id === state.productId) ?? null;
  const color = colors.find((c) => c.id === state.colorId) ?? null;
  const marking = markings.find((m) => m.id === state.markingId) ?? null;
  const mockup = product ? mockups.find((m) => m.id === product.mockup) ?? null : null;
  const quote = product
    ? computeQuote(product, Math.max(state.quantity, 1), marking, design.elements, state.logoColors)
    : null;

  const stepValid = [
    !!product,
    !!marking && !!color && (design.elements.length > 0 || state.provideLater),
    !!product && state.quantity >= product.minQty,
    true,
  ];
  const canContinue = stepValid[step];

  function selectCategory(id: string) {
    setState({ ...initialState, categoryId: id });
    design.reset();
    setMaxReached(0);
  }

  function clearCategory() {
    setState(initialState);
    design.reset();
    setMaxReached(0);
  }

  function selectProduct(p: Product) {
    if (product?.mockup !== p.mockup) design.reset();
    const cat = categories.find((c) => c.id === p.categoryId);
    setState((s) => ({
      ...s,
      productId: p.id,
      colorId: p.colors[0],
      quantity: p.minQty,
      markingId: cat?.markings[0] ?? null,
    }));
  }

  function update<K extends keyof ConfigState>(key: K, value: ConfigState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function selectMarking(id: MarkingId) {
    setState((s) => ({ ...s, markingId: id }));
  }

  function goTo(index: number) {
    if (index <= maxReached) setStep(index);
  }

  function next() {
    if (!canContinue || step >= STEPS.length - 1) return;
    const target = step + 1;
    setStep(target);
    setMaxReached((m) => Math.max(m, target));
  }

  function back() {
    if (step === 0 && state.categoryId) {
      clearCategory();
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  }

  function reset() {
    setState(initialState);
    design.reset();
    setStep(0);
    setMaxReached(0);
  }

  return {
    state,
    step,
    maxReached,
    category,
    product,
    color,
    marking,
    mockup,
    quote,
    design,
    canContinue,
    selectCategory,
    clearCategory,
    selectProduct,
    selectMarking,
    update,
    goTo,
    next,
    back,
    reset,
  };
}

export type Configurator = ReturnType<typeof useConfigurator>;
